import { bool, func, instanceOf, oneOfType, string, ReactNode } from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import moment from "moment-timezone";
import DOMPurify from 'dompurify';
import {
    RESET_CLICKS,
    SET_END_CLICK,
    SET_RECOMPUTE_ITEM_AVAILABILITY,
    SET_CART_ITEM_STATUS_CHANGED_REASON,
    SET_START_CLICK,
    UPDATE_CONSUMABLES,
} from '../../../../../../aem-core-components/actions/constants';
import { useFilterState } from '../../../../../cap';
import { getFormattedTooltipContent, getHolidayList, getWeekendDisableClass, isDateDisabled } from '../../../../utils/commonUtils';
import UnavailableRental from '../../../unavailableRental/UnavailableRental';
import { RangePickerOverlay as StorybookRangePickerOverlay } from '@sunbeltrentals/sbr-global-components';
import useAnalytics from '../../../../../../hooks/analytics/useAnalytics';
import useModalAnalytics from '../../../../../../hooks/analytics/useModalAnalytics';
import { useAnalyticsContext } from '../../../../../../config/GoogleTagManagerEvents';
import { useCartState } from '../../../../../../contexts/cart';
import { STORAGE_CONFIG } from '../../../../../../constants/storageConfig';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../../../constants/analyticsConstants/Ecommerce';
import useMedia from '../../../../../../hooks/useMedia';
import useDatePicker from '../../../../../../hooks/useDatePicker';
import {
    SET_END_DATE,
    SET_IS_CAP_DETAILS_UPDATING,
    SET_SELECTED_STORE_DETAILS,
    SET_START_DATE
} from '../../../../../cap/constants';
import { useCapUtils } from '../../../../../cap/hooks/useCapUtils';
import useCheckLocationEmpty from '../../../../../../hooks/useCheckLocationEmpty';
import { ModalContext } from '../../../../../../aem-core-components/context/ModalContext';
import { RENTAL_CHANGE_CONFIRMATION_MODAL } from '../../../../../../aem-core-components/context/Modal/constants';
import { LoadingIndicator } from '../../../../../../aem-core-components';
import { isValidString, logError } from '../../../../utils/logger';
import { getDateDiffInHrs } from '../../../../../cap/utils/atputils';
import { AUTHORITY_TYPE } from '../../../../constants';
import { useCheckAuthorityType } from '../../../../../../hooks/useCheckUser';
import { VARIABLE_CONFIG } from '../../../../../../constants/analyticsConstants/Variables';
import { useIntl } from 'react-intl';
import { ENV_CONFIG } from '../../../../../../constants/envConfig';

const RangePickerOverlayWrapper = (props) => {
    const {
        isOpen,
        minAvailableDate,
        handleCloseModal,
        showOnDrawerForTablet,
        hideBackButton,
        pickerOverlayClass,
        showAlertModal,
        isDateModalOpen,
        noPickupSlotsWarningMessage,
        minAvailableDateAlertMsg
    } = props;
    const intl = useIntl();
    const [{ calendarDateInteraction, cart, startDateClick, endDateClick }, dispatch] = useCartState();
    const [{ selectedStoreDetails, isEditView }, filterDispatch] = useFilterState();
    const { onDateClick, handleStartClick, handleEndClick, resetDates, skipErrorForSameDate, getMaxDate } = useDatePicker();
    const useModalState = () => useContext(ModalContext);
    const { openModal } = useModalState();
    const { sendEventsForDatePickerFormInteraction } = useAnalyticsContext();
    const [{ payloadEcommerceActionAnalytics }] = useAnalytics();
    const [{ sendAnalyticsDatePickerModalEvents, sendConfirmationModalAnalytics, sendConfirmationModalViewAnalytics }] =
        useModalAnalytics();
    const mediaType = useMedia();
    const { getCartUpdatedDetails, updateBSRInStorageAndContext, updateUnavailableItemsOnDateChange } = useCapUtils();
    const { fetchLocationCoordinates } = useCheckLocationEmpty();
    const { localLat, localLong } = fetchLocationCoordinates();
    const [showWarning, setShowWarning] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState(undefined);
    const [selectedEndDate, setSelectedEndDate] = useState(undefined);
    const [isEndDateEmpty, setIsEndDateEmpty] = useState(false);
    const [availableDate, setAvailableDate] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const [isStartDateEarly, setIsStartDateEarly] = useState(false);
    const [rangeState, setRangeState] = useState([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
            color: '#F2F2F2'
        }
    ]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const authorityType = useCheckAuthorityType();
    const isP2P = [AUTHORITY_TYPE.P2P].includes(authorityType);
    const { holidaydatescoachmarkflag } = ENV_CONFIG.COACHMARK_STATUS || {};

    useEffect(() => {
        setIsModalOpen(isOpen);
    }, [isOpen]);

    useEffect(() => {
        if (minAvailableDate) {
            setSelectedStartDate(undefined);
            setSelectedEndDate(undefined);
            setRangeState([
                {
                    key: 'selection',
                    startDate: undefined,
                    endDate: undefined,
                    color: '#F2F2F2'
                }
            ]);
            dispatch({ type: SET_START_CLICK });
            setShowWarning(true);
            setAvailableDate(minAvailableDate);
        } else {
            setSelectedStartDate(new Date(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE)) || undefined);
            setSelectedEndDate(new Date(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ENDDATE)) || undefined);
            setRangeState([
                {
                    key: 'selection',
                    startDate: localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE)
                        ? new Date(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE))
                        : undefined,
                    endDate: localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ENDDATE)
                        ? new Date(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ENDDATE))
                        : undefined,
                    color: '#F2F2F2'
                }
            ]);
        }
    }, [minAvailableDate]);

    useEffect(() => {
        //if pickup slot is not available then show warning message.
        if (noPickupSlotsWarningMessage) {
            dispatch({ type: SET_END_CLICK });
            setShowWarning(true);
        }
    }, [noPickupSlotsWarningMessage]);

    useEffect(() => {
        if (!showWarning && availableDate) {
            setAvailableDate('');
        }
    }, [showWarning]);

    useEffect(() => {
        try {
          if (calendarDateInteraction) {
            const startDate = rangeState?.[0]?.startDate;
            const endDate = rangeState?.[0]?.endDate;
      
            if (startDate) {
              handleDateEventsInteraction?.(
                startDate.toString(),
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_START_DATE_FORM_FIELD
              );
            }
      
            if (endDate) {
              handleDateEventsInteraction?.(
                endDate.toString(),
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_END_DATE_FORM_FIELD
              );
            }
          }
        } catch (error) {
          logError(error, false, 'rangeState date interaction', [rangeState]);
        }
      }, [rangeState, calendarDateInteraction]);

    const handleCalendarClose = (type) => {
        if (isOpen && rangeState?.[0]?.startDate && !rangeState?.[0]?.endDate) {
            setIsEndDateEmpty(true);
            setShowWarning(true);
            return;
        }
        setShowWarning(false);
        setIsEndDateEmpty(false);
        handleCloseModal();
        const body = document.querySelector('body');
        body.classList.remove('calender-overflow-hidden');
        body.classList.remove('ReactModal__Body--open');
    };

    const isHolidayDate = isDateDisabled();

    const customOnModalOpen = () => {
        if (!isNaN(new Date(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE)).getTime())) {
            if (
                new Date(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE)).getTime() <
                new Date().setHours(0, 0, 0, 0)
            ) {
                localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE, '');
                localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.ENDDATE, '');
                filterDispatch({ type: SET_START_DATE, startDate: '' });
                filterDispatch({ type: SET_END_DATE, endDate: '' });

                return true;
            } else {
                return false;
            }
        } else return false;
    };

    const checkIfDayDisabled = date => {
        return isHolidayDate(date);
    };

    const onRentalChangeConfirmation = async (
        isAccepted,
        startDate,
        endDate,
        cartDetails,
        isCloseClicked,
        isBackClicked,
        consumablesData,
        unavailableConsumables
    ) => {
        /* To remove the details unavailable items from the cart if user agree to proceed with updated rental details*/
        if (isAccepted) {
            filterDispatch({ type: SET_IS_CAP_DETAILS_UPDATING, isCapDetailsUpdating: true });
            //commenting it as we will have t display unavailable items in cart
            //await removeUnavailableItemsFromCart(cartDetails);
            /* To update the date and store detials in context*/
            saveDateInContext(startDate, endDate);
            if (consumablesData) {
                dispatch({ type: UPDATE_CONSUMABLES, key: 'dataWithoutPrice', value: consumablesData });
            }
            filterDispatch({ type: SET_IS_CAP_DETAILS_UPDATING, isCapDetailsUpdating: false });
        }
        sendConfirmationModalAnalytics(isAccepted, isCloseClicked, isBackClicked, cartDetails, unavailableConsumables);
    };
    const updateDateContext = async (startDate, endDate) => {
        try {
            /* To show the alert modal when rental details are chnaged and items in the cart are there*/
            const diffInHours = getDateDiffInHrs(startDate, moment().format('YYYY-MM-DDTHH:mm:ss'));
            if (startDate && new Date(startDate).getTime() == new Date(endDate).getTime()) {
                setShowWarning(true);
                return false;
            } else if (!endDate || !startDate) {
                setIsEndDateEmpty(true);
                setShowWarning(true);
                return false;
            } else if (diffInHours <= 48 && isP2P) {
                setIsStartDateEarly(true);
                setShowWarning(true);
                return false;
            } else {
                if (
                    (showAlertModal &&
                        isValidString(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY)) > 0) ||
                    cart?.total_quantity > 0
                ) {
                    setIsModalOpen(false);
                    setShowLoader(true);
                    const storeDetailData = JSON.parse(
                        localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.LOCALSELECTEDSTOREDETAILS) || '{}'
                    );
                    /* Fetch the changed details from the compare estimates graphQL*/
                    const { cartDetails, consumablesData, unavailableConsumables } = await getCartUpdatedDetails({
                        filterStartDate: startDate,
                        filterEndDate: endDate,
                        selectedPickupStore: isDateModalOpen ? storeDetailData : selectedStoreDetails
                    });
                    setShowLoader(false);
                    if (
                        cartDetails?.length > 0 ||
                        unavailableConsumables?.unavailableAccessories?.length > 0 ||
                        unavailableConsumables?.unavailableAddons?.length > 0
                    ) {
                        openModal(RENTAL_CHANGE_CONFIRMATION_MODAL, {
                            isOpen: true,
                            productList: cartDetails,
                            accessoriesList: unavailableConsumables?.unavailableAccessories,
                            addonsList: unavailableConsumables?.unavailableAddons,
                            handleConfirmation: (isAccepted, isCloseClicked, isBackClicked) =>
                                onRentalChangeConfirmation(
                                    isAccepted,
                                    startDate,
                                    endDate,
                                    cartDetails,
                                    isCloseClicked,
                                    isBackClicked,
                                    consumablesData,
                                    unavailableConsumables
                                )
                        });
                        sendConfirmationModalViewAnalytics(cartDetails, unavailableConsumables);
                        return false;
                    }
                }
                saveDateInContext(startDate, endDate);
                return true;
            }
        } catch (error) {
            logError(error, false, 'updateDateContext', [startDate, endDate]);
        }
    };

    const saveDateInContext = async (startDate, endDate) => {
        /* Update the date and store detials in the context when user click on done button*/
        filterDispatch({
            type: SET_START_DATE,
            startDate: isNaN(new Date(startDate).getDate()) ? '' : startDate.toString()
        });
        filterDispatch({ type: SET_END_DATE, endDate: isNaN(new Date(endDate).getDate()) ? '' : endDate.toString() });
        // removing bsr if dates are greater than 24 hrs
        const dateDiff = getDateDiffInHrs(startDate, moment().format('YYYY-MM-DDTHH:mm:ss'));
        if (dateDiff > 24) {
            updateBSRInStorageAndContext();
        }
        updateUnavailableItemsOnDateChange(startDate);

        /* Update the selected store details for change store and change date CTA when done is clicked  */
        const storeDetailData = JSON.parse(
            localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.LOCALSELECTEDSTOREDETAILS) || '{}'
        );
        if (isDateModalOpen) {
            filterDispatch({
                type: SET_SELECTED_STORE_DETAILS,
                selectedStoreDetails: storeDetailData,
                localLat,
                localLong
            });
            localStorage.removeItem(STORAGE_CONFIG.LOCAL_STORAGE.LOCALSELECTEDSTOREDETAILS);
        }

        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.STARTDATE, startDate);
        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.ENDDATE, endDate);
        sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CART_ITEMS);
        dispatch({ type: SET_RECOMPUTE_ITEM_AVAILABILITY, recomputeItemsAvailability: true });
        dispatch({
            type: SET_CART_ITEM_STATUS_CHANGED_REASON,
            cartItemsAvailabilityChangedReason: VARIABLE_CONFIG.CART_ITEM_STATUS_CHANGED_REASON.DATES_DETAILS_CHANGED
        });
        sessionStorage.setItem(
            STORAGE_CONFIG.SESSION_STORAGE.CART_ITEMS_AVAILABILITY_CHANGED_REASON,
            VARIABLE_CONFIG.CART_ITEM_STATUS_CHANGED_REASON.DATES_DETAILS_CHANGED
        );
    };

    const handleResetClicked = () => {
        dispatch({ type: RESET_CLICKS });
    };

    const handleDateEventsInteraction = (date, fieldName) => {
        sendEventsForDatePickerFormInteraction(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_DATE_PICKER_INTERACTION,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_DATE_FORM_NAME,
            fieldName,
            date,
            payloadEcommerceActionAnalytics(false)
        );
    };

    const sendDatePickerModalEvents = (eventName, startDate, endDate) => {
        sendAnalyticsDatePickerModalEvents(eventName, startDate, endDate);
    };

    const getTooltipContent = () => {
        return (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getFormattedTooltipContent()) }} />
        );
    };

    return (
        <>
            <StorybookRangePickerOverlay
                isOpen={isModalOpen || false}
                showWarning={showWarning}
                setShowWarning={setShowWarning}
                setIsEndDateEmpty={setIsEndDateEmpty}
                rangeState={rangeState}
                setRangeState={setRangeState}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                setSelectedStartDate={setSelectedStartDate}
                setSelectedEndDate={setSelectedEndDate}
                handleToggle={handleCalendarClose}
                customOnModalOpen={customOnModalOpen}
                checkIfDayDisabled={checkIfDayDisabled}
                handleSelectedDatesOnDone={updateDateContext}
                onResetClick={handleResetClicked}
                sendDatePickerModalEvents={sendDatePickerModalEvents}
                calendarDateInteraction={calendarDateInteraction || false}
                minDate={minAvailableDate? new Date(minAvailableDate) : new Date()}
                maxDate={getMaxDate()}
                showOnDrawerForTablet={showOnDrawerForTablet || false}
                hideBackButton={hideBackButton || true}
                pickerOverlayClass={pickerOverlayClass || ''}
                setIsStartDateEarly={setIsStartDateEarly}
                renderWarningComponent={
                    <UnavailableRental
                        showWarning={showWarning}
                        isOpen={isOpen}
                        handleToggle={handleCalendarClose}
                        isEndDateEmpty={isEndDateEmpty}
                        minAvailableDate={availableDate}
                        isStartDateEarly={isStartDateEarly}
                        noPickupSlotsWarningMessage={noPickupSlotsWarningMessage}
                        minAvailableDateAlertMsg={minAvailableDateAlertMsg}
                    />
                }
                startDateLabel={intl.formatMessage({ id: 'cart-header:start-date' })}
                endDateLabel={intl.formatMessage({ id: 'cart-header:end-date' })}
                isEditView={isEditView || false}
                resetDates={resetDates}
                startDateClick={startDateClick || false}
                endDateClick={endDateClick || false}
                resetDatesText={intl.formatMessage({ id: 'calendar:reset-dates' })}
                doneBtnText={intl.formatMessage({ id: 'common:done' })}
                calendarHeading={intl.formatMessage({ id: 'calendar-header-select-your-rental-dates' })}
                variableConfigs={{
                    EVENT: VARIABLE_CONFIG.EVENT,
                    CAP_VIEW_MODE: VARIABLE_CONFIG.CAP_VIEW_MODE
                }}
                mediaType={mediaType}
                handleStartClick={()=> handleStartClick(handleCalendarClose, isOpen)}
                handleEndClick={()=>handleEndClick(handleCalendarClose, isOpen)}
                onDateClick={() => onDateClick(setIsEndDateEmpty, setIsStartDateEarly)}
                skipErrorForSameDate={()=>skipErrorForSameDate(selectedStartDate, selectedEndDate, showWarning)}
                holidays={getHolidayList()}
                showTooltipOnClick={isValidString(holidaydatescoachmarkflag) && !isP2P}
                tooltipContent={getTooltipContent()}
                weekendDisableClass={getWeekendDisableClass()}
                holidayCoachmarkClass="rangeCalender_tooltip"
            />
            {showLoader && (
                <div className="cap-date-overlay">
                    <LoadingIndicator global />
                </div>
            )}
        </>
    );
};

export default React.memo(RangePickerOverlayWrapper);

RangePickerOverlayWrapper.defaultProps = {
    isOpen: false,
    handleCloseModal: () => {},
    showOnDrawerForTablet: false,
    hideBackButton: false,
    pickerOverlayClass: '',
    showAlertModal: false,
    isDateModalOpen: false,
    noPickupSlotsWarningMessage: null,
    minAvailableDateAlertMsg: ''
};

RangePickerOverlayWrapper.propTypes = {
    isOpen: bool,
    minAvailableDate: oneOfType([string, instanceOf(Date)]),
    handleCloseModal: func,
    showOnDrawerForTablet: bool,
    hideBackButton: bool,
    pickerOverlayClass: string,
    showAlertModal: bool,
    isDateModalOpen: bool,
    noPickupSlotsWarningMessage: ReactNode,
    minAvailableDateAlertMsg: string
};
